import { useEffect, useState } from 'react'
import { FilePlus2, LayoutTemplate } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { cn } from 'lib/utils'

import * as presentationService from '../services/presentationService'
import type { AssetOption, PresentationTemplate } from '../types/presentation.types'
import { AssetPicker } from './AssetPicker'
import { DeckTemplateGallery } from './DeckTemplateGallery'

export type PresentationDialogMode = 'create' | 'rename'

/** How a new deck starts (MYD-604). */
type Start = 'blank' | 'template'

export interface FromTemplateValues {
	templateKey: string
	asset: AssetOption
	/** Absent: the server names it "<template>: <asset>". */
	name?: string
}

interface NewPresentationDialogProps {
	open: boolean
	mode: PresentationDialogMode
	/** Pre-fills the name when renaming. */
	initialName?: string
	isSaving?: boolean
	error?: string | null
	onClose: () => void
	onSubmit: (values: { name: string; asset: AssetOption | null }) => void
	/**
	 * Start from a deck template (MYD-604). Without it the dialog offers a
	 * blank deck only, as before.
	 */
	onCreateFromTemplate?: (values: FromTemplateValues) => void
	/**
	 * The asset the deck is for, fixed: from an asset's page. Template path
	 * only, showing only templates for this kind of asset.
	 */
	presetAsset?: AssetOption | null
}

const COPY: Record<PresentationDialogMode, { title: string; description: string; action: string; busy: string }> = {
	create: {
		title: 'New presentation',
		description: 'Start blank, or from a template that builds the stories for you.',
		action: 'Create',
		busy: 'Creating...',
	},
	rename: {
		title: 'Rename presentation',
		description: 'Only the name changes.',
		action: 'Rename',
		busy: 'Saving...',
	},
}

/**
 * Create a deck, or rename one (MYD-494). One dialog for both, as Explorer's
 * SaveReportDialog does, because they differ only in the asset picker and the
 * wording.
 *
 * A new deck starts blank or from a deck template (MYD-604). The template path
 * is: choose a template, choose the asset it is for, Create. The server builds
 * every story, so the deck opens with them already bound.
 *
 * `components/ui/dialog.tsx` is a hand-rolled portal, not Radix: its prop is
 * `onClose`, and it renders nothing while closed.
 */
export function NewPresentationDialog({
	open,
	mode,
	initialName = '',
	isSaving = false,
	error,
	onClose,
	onSubmit,
	onCreateFromTemplate,
	presetAsset = null,
}: NewPresentationDialogProps) {
	const offersTemplates = mode === 'create' && Boolean(onCreateFromTemplate)
	const [start, setStart] = useState<Start>('blank')
	const [name, setName] = useState(initialName)
	const [asset, setAsset] = useState<AssetOption | null>(null)
	const [templates, setTemplates] = useState<PresentationTemplate[] | null>(null)
	const [template, setTemplate] = useState<PresentationTemplate | null>(null)
	const [loadError, setLoadError] = useState<string | null>(null)

	// Reset on open, so a second New does not show the first one's choices.
	useEffect(() => {
		if (open) {
			setStart(offersTemplates && presetAsset ? 'template' : 'blank')
			setName(initialName)
			setAsset(presetAsset)
			setTemplate(null)
			// Reloaded on every open: one saved from an editor since must show.
			setTemplates(null)
			setLoadError(null)
		}
	}, [open, initialName, offersTemplates, presetAsset])

	// The templates, the first time the template path is shown.
	useEffect(() => {
		if (!open || start !== 'template' || templates !== null) return
		let cancelled = false
		presentationService
			.listDeckTemplates()
			.then(list => !cancelled && setTemplates(list))
			.catch(() => {
				if (cancelled) return
				setTemplates([])
				setLoadError('Templates could not be loaded.')
			})
		return () => {
			cancelled = true
		}
	}, [open, start, templates])

	const copy = COPY[mode]
	const trimmed = name.trim()
	const fromTemplate = offersTemplates && start === 'template'

	// The template path needs a template and an asset of its kind; the name is
	// optional. A blank deck needs only a name.
	const canSubmit = !isSaving && (fromTemplate ? Boolean(template && asset && asset.scope === template.scope) : trimmed.length > 0)

	const submit = () => {
		if (!canSubmit) return
		if (fromTemplate && template && asset) {
			onCreateFromTemplate?.({ templateKey: template.key, asset, ...(trimmed ? { name: trimmed } : {}) })
			return
		}
		onSubmit({ name: trimmed, asset })
	}

	// Picking an asset before naming a BLANK deck fills the name in, since "the
	// Hollister pitch" is usually about Hollister. A name already typed wins. A
	// template deck is named by the server instead.
	const pickAsset = (next: AssetOption | null) => {
		setAsset(next)
		if (!fromTemplate && next && !name.trim()) setName(next.name)
	}

	// A template for a different kind of asset clears an asset of the old kind.
	const pickTemplate = (next: PresentationTemplate) => {
		setTemplate(next)
		if (!presetAsset && asset && asset.scope !== next.scope) setAsset(null)
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className={cn(fromTemplate ? 'sm:max-w-[720px]' : 'sm:max-w-[520px]')}>
				<DialogHeader>
					<DialogTitle>{presetAsset && fromTemplate ? `New deck for ${presetAsset.name}` : copy.title}</DialogTitle>
					<DialogDescription>{copy.description}</DialogDescription>
				</DialogHeader>

				<div className="space-y-5 px-5 py-4">
					{/* From an asset's page the deck is a template's: no blank choice. */}
					{offersTemplates && !presetAsset && (
						<div role="radiogroup" aria-label="How to start" className="grid grid-cols-2 gap-2">
							{(
								[
									['blank', 'Blank deck', FilePlus2],
									['template', 'Start from template', LayoutTemplate],
								] as const
							).map(([value, label, Icon]) => (
								<button
									key={value}
									type="button"
									role="radio"
									aria-checked={start === value}
									onClick={() => setStart(value)}
									className={cn(
										'flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
										start === value ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-700 hover:bg-gray-50',
									)}
								>
									<Icon className="h-4 w-4" /> {label}
								</button>
							))}
						</div>
					)}

					{fromTemplate && (
						<div className="space-y-2">
							<Label>Template</Label>
							<DeckTemplateGallery templates={templates} scope={presetAsset?.scope} selectedKey={template?.key ?? null} onSelect={pickTemplate} />
							{loadError && <p className="text-sm text-destructive">{loadError}</p>}
						</div>
					)}

					{fromTemplate && !presetAsset && (
						<div className="space-y-2">
							<Label>For</Label>
							{template ? (
								<AssetPicker key={template.scope} value={asset} onChange={pickAsset} scopes={[template.scope]} />
							) : (
								<p className="text-sm text-muted-foreground">Choose a template first.</p>
							)}
						</div>
					)}

					<div className="space-y-2">
						<Label htmlFor="presentation-name">
							Name {fromTemplate && <span className="font-normal text-muted-foreground">(optional)</span>}
						</Label>
						<Input
							id="presentation-name"
							value={name}
							maxLength={140}
							autoFocus={!fromTemplate}
							onChange={event => setName(event.target.value)}
							onKeyDown={event => {
								if (event.key === 'Enter') submit()
							}}
							placeholder={
								fromTemplate
									? template && asset
										? `${template.name}: ${asset.name}`
										: 'Named after the template and the asset'
									: 'Hollister Crossing pitch'
							}
						/>
						{error && <p className="text-sm text-destructive">{error}</p>}
					</div>

					{mode === 'create' && !fromTemplate && (
						<div className="space-y-2">
							<Label>
								First story <span className="font-normal text-muted-foreground">(optional)</span>
							</Label>
							<AssetPicker value={asset} onChange={pickAsset} />
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={onClose} disabled={isSaving}>
						Cancel
					</Button>
					<Button onClick={submit} disabled={!canSubmit}>
						{isSaving ? copy.busy : copy.action}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default NewPresentationDialog
